import { ContentState } from "draft-js";
import { Observable, WritableObservable } from "micro-observables";
import { GroupAccessMode, GroupDraft, GroupVisibility } from "@domain/group/groupDraft";
import { Category } from "@domain/lms/category/category";
import { Media } from "@domain/media/media";
import { Organization } from "@domain/organization/organization";

export const groupEditionSteps = ["INFOS", "MEMBERS"] as const;
export type GroupEditionStep = (typeof groupEditionSteps)[number];
export const MAX_SHAREROOM_NAME_LENGTH = 75;
export const MAX_SHAREROOM_DESCRIPTION_LENGTH = 120;

export class GroupEditorController {
	private previousValues: GroupDraft[];
	private readonly currentValueIndex = new WritableObservable(0);

	readonly group: Observable<GroupDraft>;
	private _step = new WritableObservable<GroupEditionStep>("INFOS");
	readonly step = this._step.readOnly();

	constructor(initialGroupDraft: GroupDraft) {
		this.previousValues = [initialGroupDraft];
		this.group = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	next(): void {
		const step = this.step.get();
		if (step === "INFOS") {
			this._step.set("MEMBERS");
		}
	}

	previous(): void {
		const step = this.step.get();
		if (step === "MEMBERS") {
			this._step.set("INFOS");
		}
	}

	updateGroup(groupUpdate: Partial<GroupDraft>): void {
		const group: GroupDraft = { ...this.group.get(), ...groupUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), group];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	setName(name: string): void {
		this.updateGroup({ name });
	}
	setDescription(description: string): void {
		this.updateGroup({ description });
	}
	setMainCategory(category?: Category): void {
		this.updateGroup({ categories: category ? [category] : [] });
	}
	setRules(rules: ContentState): void {
		this.updateGroup({ rules });
	}
	setBanner(banner?: Media): void {
		this.updateGroup({ banner });
	}
	setOrganization(organization?: Organization): void {
		this.updateGroup({ organization });
	}
	setPrivacy(visibility: GroupVisibility): void {
		this.updateGroup({ visibility });
	}
	setAccessMode(accessMode: GroupAccessMode): void {
		this.updateGroup({ accessMode });
	}

	get isValid(): Observable<boolean> {
		return Observable.from(this.group).transform(([group]) => {
			if (
				!group.name ||
				!group.description ||
				!group.categories.length ||
				group.name.length > MAX_SHAREROOM_NAME_LENGTH ||
				group.description.length > MAX_SHAREROOM_DESCRIPTION_LENGTH
			) {
				return false;
			}
			return true;
		});
	}
}
